import React from 'react';
import { Alert, Spin, Tag, Tooltip } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

/**
 * Display name for a resolved contact: full name, falling back to the company
 * name and finally to the address itself, so a tag is never blank.
 */
export const contactLabel = (contact) => {
  const name = [contact?.first_name, contact?.last_name].filter(Boolean).join(' ').trim();
  return name || contact?.company_name || contact?.email || '';
};

/**
 * Compact "who will get this email" list - one `Name (email)` tag per recipient.
 * Fed by the `recipients` the preview endpoint returns, which is resolved by the
 * same backend code that performs the send.
 */
export const RecipientsSummary = ({ recipients = [], buildingsWithoutContacts = [] }) => {
  const { t } = useTranslation();

  return (
    <div style={{ marginBottom: 12 }}>
      {recipients.length > 0 ? (
        <div
          style={{
            background: '#fafafa',
            border: '1px solid #f0f0f0',
            borderRadius: 4,
            padding: '8px 10px',
          }}
        >
          <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
            {t('clientInform.willBeSentTo', { count: recipients.length })}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {recipients.map((recipient) => {
              const addresses = (recipient.building_units || [])
                .map((unit) => unit.full_address)
                .filter(Boolean);

              return (
                <Tooltip
                  key={recipient.email}
                  title={addresses.length ? addresses.join(', ') : null}
                  placement="top"
                >
                  <Tag style={{ margin: 0, fontSize: 12 }}>
                    {contactLabel(recipient)} ({recipient.email})
                  </Tag>
                </Tooltip>
              );
            })}
          </div>
        </div>
      ) : (
        <Alert type="warning" showIcon message={t('clientInform.noRecipientsResolved')} />
      )}

      {buildingsWithoutContacts.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message={t('clientInform.buildingsWithoutContacts', {
            buildings: buildingsWithoutContacts.join(', '),
          })}
        />
      )}
    </div>
  );
};

/**
 * Table cell for the "recipients" column. `contacts` are the contacts the backend
 * resolved for this specific building unit - not every contact on the building.
 */
export const RecipientsCell = ({ contacts, loading }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div style={{ textAlign: 'center', color: '#999' }}>
        <Spin size="small" indicator={<LoadingOutlined spin />} />
      </div>
    );
  }

  if (!contacts || contacts.length === 0) {
    return (
      <span style={{ color: '#999', fontSize: '12px' }}>
        {t('clientInform.noContacts')}
      </span>
    );
  }

  return (
    <div style={{ fontSize: '12px' }}>
      {contacts.map((contact, index) => (
        <Tooltip
          key={contact.id || index}
          title={
            <div>
              {contact.phone && <div><strong>{t('clientInform.phone')}:</strong> {contact.phone}</div>}
              {contact.company_name && <div><strong>{t('clientInform.company')}:</strong> {contact.company_name}</div>}
              {!contact.phone && !contact.company_name && <div>{t('clientInform.noAdditionalInfo')}</div>}
            </div>
          }
          placement="topLeft"
          mouseEnterDelay={0.2}
        >
          <div style={{ marginBottom: '2px', cursor: 'help' }}>
            {contactLabel(contact)} <span style={{ color: '#888' }}>({contact.email})</span>
          </div>
        </Tooltip>
      ))}
    </div>
  );
};
